'use client'

import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Boxes, Check, ArrowRight, Zap } from 'lucide-react'

interface Feature {
  title: string
  description: string
}

interface AutomotiveInventoryEngineSectionProps {
  eyebrow?: string
  heading: string
  intro?: string
  features: Feature[]
  proof?: string
  ctaLabel?: string
  ctaHref?: string
}

export function AutomotiveInventoryEngineSection({
  eyebrow,
  heading,
  intro,
  features,
  proof,
  ctaLabel,
  ctaHref,
}: AutomotiveInventoryEngineSectionProps) {
  return (
    <section className="relative py-24 overflow-hidden bg-background">
      <div className="container mx-auto px-6">
        <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-start max-w-6xl mx-auto">
          {/* Left: pitch */}
          <div>
            {eyebrow && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="reveal inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6"
              >
                <Boxes className="h-4 w-4 text-primary" />
                <span className="eyebrow">{eyebrow}</span>
              </motion.div>
            )}
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="reveal reveal-delay-1 text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight"
            >
              {heading}
            </motion.h2>
            {intro && (
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="reveal reveal-delay-2 text-lg text-muted-foreground mb-8"
              >
                {intro}
              </motion.p>
            )}
            {proof && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="rounded-2xl border border-primary/20 bg-primary/5 p-6"
              >
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.3em] text-primary mb-3">
                  <Zap className="h-4 w-4" />
                  Live in production
                </div>
                <p className="text-sm text-foreground leading-relaxed">{proof}</p>
                {ctaLabel && ctaHref && (
                  /^https?:\/\//.test(ctaHref) ? (
                    <a
                      href={ctaHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-primary hover:gap-2.5 transition-all"
                    >
                      {ctaLabel}
                      <ArrowRight className="h-4 w-4" />
                    </a>
                  ) : (
                    <Link
                      to={ctaHref}
                      className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-primary hover:gap-2.5 transition-all"
                    >
                      {ctaLabel}
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  )
                )}
              </motion.div>
            )}
          </div>

          {/* Right: feature cards */}
          <div className="grid gap-4 sm:grid-cols-2">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.07, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="reveal rounded-2xl border border-border/50 bg-white dark:bg-zinc-900 p-6 transition-all duration-300 lift-hover"
                style={{ transitionDelay: `${(index % 4) * 80}ms` }}
              >
                <Check className="h-5 w-5 text-primary mb-3" />
                <h3 className="text-base font-bold text-foreground mb-1.5">{feature.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
